export type AgentSettingsScope =
  | 'FIRM'
  | 'VERTICAL'
  | 'CLIENT'
  | 'PROJECT_PHASE'

export type AgentPhase =
  | 'discover'
  | 'design'
  | 'build'
  | 'migrate'
  | 'test'
  | 'go-live'
  | 'maintain'

/** The seven lifecycle phases in their fixed order (per-phase settings rows). */
export const AGENT_PHASES: readonly AgentPhase[] = [
  'discover',
  'design',
  'build',
  'migrate',
  'test',
  'go-live',
  'maintain',
]

export type AgentAutonomyRung =
  | 'L1_SUGGESTS'
  | 'L2_DRAFTS'
  | 'L3_GUARDED_ACT'
  | 'L4_ACTS_AND_REPORTS'
  | 'SYSTEM_DEFAULT'

export interface ReviewPolicySettings {
  requiresNamedHumanMerge?: true
  secondReviewerRule?: string
  escalationHours?: number
}

export interface AgentSettingsIdentity {
  scope: AgentSettingsScope
  scopeKey?: string
  phase: AgentPhase
  agentKey: string
}

export interface AgentSettingsKey extends AgentSettingsIdentity {
  orgId: string
}

/** A server-derived CLIENT rung identity; scopeKey is opaque and project-compatible. */
export interface AgentSettingsClientScope {
  scopeKey: string
  clientId: string
  name: string
}

export interface AgentSettingsDraft extends AgentSettingsIdentity {
  autonomyRung?: AgentAutonomyRung
  costCeilingPerRunMinor?: number
  costCeilingPerPhaseMinor?: number
  currency?: string
  policyPresetKey?: string
  reviewPolicy?: ReviewPolicySettings
  clientOverrideReason?: string
  outputPermissions?: AgentOutputPermission[]
  draftVersion: number
  configuredBy?: string
  configuredByName?: string
  configuredAt?: number
  createdAt?: number
}

export interface AgentSettingsVersion extends AgentSettingsIdentity {
  version: number
  autonomyRung?: AgentAutonomyRung
  costCeilingPerRunMinor?: number
  costCeilingPerPhaseMinor?: number
  currency?: string
  policyPresetKey?: string
  reviewPolicy?: ReviewPolicySettings
  clientOverrideReason?: string
  outputPermissions?: AgentOutputPermission[]
  payloadDigest: string
  publishedBy: string
  publishedByName?: string
  publishedAt: number
  publishReason?: string
}

/** Exact writable DTO: server-owned timestamps, actors and versions are absent. */
export interface AgentSettingsDraftWrite extends AgentSettingsIdentity {
  baseVersion: number
  autonomyRung?: AgentAutonomyRung | null
  costCeilingPerRunMinor?: number | null
  costCeilingPerPhaseMinor?: number | null
  currency?: string | null
  policyPresetKey?: string | null
  reviewPolicy?: ReviewPolicySettings | null
  clientOverrideReason?: string | null
  outputPermissions?: AgentOutputPermission[] | null
}

/** The server-owned resolution returned by the dry-run endpoint. */
export interface EffectiveAgentSettings {
  projectId?: string
  phase: AgentPhase
  agentKey: string
  autonomyRung: AgentAutonomyRung
  costCeilingPerRunMinor?: number
  costCeilingPerPhaseMinor?: number
  currency?: string
  policyPresetKey?: string
  reviewPolicy?: ReviewPolicySettings
  clientOverrideReason?: string
  outputPermissions?: AgentOutputPermission[]
  provenance: Partial<
    Record<
      | 'autonomyRung'
      | 'costCeilingPerRunMinor'
      | 'costCeilingPerPhaseMinor'
      | 'currency'
      | 'policyPresetKey'
      | 'reviewPolicy'
      | 'clientOverrideReason'
      | 'outputPermissions',
      AgentSettingsScope | 'SYSTEM_DEFAULT'
    >
  >
  redacted?: boolean
}

/** Published FIRM-only resolution; never inferred from a draft or version. */
export type FirmEffectiveAgentSettings = EffectiveAgentSettings

export type ConnectorPermissionMode =
  | 'allowed_internal'
  | 'requires_locked_scope'
  | 'refused'
  | 'runtime_classification'

/** Stable server wire keys; browser code never derives their policy. */
export type AgentOutputPermission = 'client-read' | 'client-write' | 'message'

export interface ConnectorToolPolicyView {
  toolName: string
  connector: string
  operation: string
  target: string | null
  targetDependsOnArgs: boolean
  permissionMode: ConnectorPermissionMode
  allowedWithoutScope: boolean
  scopeGrantable: boolean
  overrideable: boolean
  refusalReason: string | null
}

export interface ConnectorPermissionView {
  key: string
  operation: string | null
  targets: string[]
  permissionMode: ConnectorPermissionMode
  allowedWithoutScope: boolean
  scopeGrantable: boolean
  overrideable: boolean
  refusalReason: string | null
}

/** Immutable W4.12 policy projection. It is display truth, never a client grant. */
export interface ConnectorPolicyView {
  tools: ConnectorToolPolicyView[]
  unknownTool: ConnectorPermissionView
  unconditionalRefusals: ConnectorPermissionView[]
  outputPermissions?: ConnectorPermissionView[]
}

/** One server-renderable change from the published version to the candidate. */
export interface AgentSettingsDiff {
  field: string
  currentValue: string | null
  candidateValue: string | null
}

export interface AgentSettingsDryRunResult {
  resolved: EffectiveAgentSettings
  diff: AgentSettingsDiff[]
  candidateDigest: string
  noChange: boolean
}

/** Publish has no value fields: the server freezes the saved draft, not this request. */
export interface AgentSettingsPublishInput extends AgentSettingsIdentity {
  baseVersion: number
  expectedDigest: string
}

/** An opaque dry-run digest, tied only to the current canonical draft equality key. */
export interface DryRunEligibility {
  settingsKey: string
  draftFingerprint: string
  candidateDigest: string
  noChange: boolean
}

export interface AgentSettingsFeatureError {
  operation:
    | 'listDrafts'
    | 'listVersions'
    | 'firmEffective'
    | 'connectorPolicy'
    | 'saveDraft'
    | 'dryRun'
    | 'publish'
  status: number
  code?: string
  message: string
  details?: unknown
}

export interface AgentSettingsEntry {
  draft: AgentSettingsDraft | null
  effective: FirmEffectiveAgentSettings | null
  versions: AgentSettingsVersion[]
  dryRun: AgentSettingsDryRunResult | null
  eligibility: DryRunEligibility | null
}

export const agentSettingsKeyOf = (key: AgentSettingsKey): string =>
  [key.orgId, key.scope, key.scopeKey ?? '', key.phase, key.agentKey].join('|')
